import {
  INSTRUCTION_EMBEDDED,
  INSTRUCTION_REDIRECT_URL,
} from '@packages/domain/constants/instructions';
import type { Currency } from '@packages/domain/models/currency';
import type { Instruction } from '@packages/domain/models/instruction';
import type { Link } from '@packages/domain/models/link';
import type { Price } from '@packages/domain/models/price';
import {
  type QueryKey,
  type UseQueryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { BILL_PAY_PAYMENT_METHOD, BPAY_PAYMENT_METHOD } from 'constants/offers';
import {
  FETCH_PAYMENT_FAILURE,
  FETCH_PAYMENT_REQUEST,
  INVALID_STUDENT_CNY_CODE,
  PAYER_NOT_SUPPORTED_CODE,
  PAYMENT_STATUS_CANCELLED,
  PAYMENT_STATUS_DELIVERED,
  PAYMENT_STATUS_FAILED,
  PAYMENT_STATUS_INITIATED,
  PAYMENT_STATUS_ON_HOLD,
  PAYMENT_STATUS_PENDING,
  PAYMENT_STATUS_RECEIVED,
  PAYMENT_STATUS_SENT,
  PAYMENT_STATUS_VERIFICATION,
} from 'constants/payment';
import type { CurrencyAmount } from 'models/currencyAmount';
import type { ExtraFee } from 'models/extraFee';
import type { Offer } from 'models/offer';
import type { Payment } from 'models/payment';
import { useCallback, useMemo } from 'react';
import { useSelector, useStore } from 'react-redux';
import type { RootState } from 'reducers/types';
import { getRecipient } from 'selectors/entities/recipient/recipient';
import { getUIPaymentStatus } from 'selectors/entities/utils/payment/payment';
import { getCurrentLocale, untilIsFetched } from 'selectors/ui/ui';
import { useDispatch } from 'store/configureStore';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';
import { type PaymentSource, usePaymentSource } from './PaymentSourceContext';
import { paymentQueries } from './paymentQueries';

type FetchPaymentByOrderParams = {
  orderId?: string;
  token?: string;
};

type FetchPaymentByOrderResult = {
  success: boolean;
  payment?: Payment;
};

type PaymentFields = {
  status?: string;
  rawStatus?: string;
  isPending: boolean;
  isOnHold: boolean;
  isDelivered: boolean;
  isReceived: boolean;
  isCancelled: boolean;
  isSent: boolean;
  isVerification: boolean;
  isFailed: boolean;
  isOnline: boolean;
  isBankTransfer: boolean;
  isProofNeeded: boolean;
  isWaitingForInstructions: boolean;
  isFromAgents: boolean;
  hasFXFormResponses: boolean;
  price?: CurrencyAmount;
  purchase?: CurrencyAmount;
  fees?: CurrencyAmount;
  extraFees: ExtraFee[];
  instructions: Instruction[];
  offer: Offer;
  paymentOption?: string;
  recurringDetails?: { total: number; current: number };
  onHoldComment?: string;
  cardDetails?: { brand: string; lastFourDigits: string };
  cancelledAt?: string | null;
  deliveredAt?: string | null;
  guaranteedAt?: string | null;
  verificationErrorCodes: string[];
  instructionsErrorCodes?: string[];
  senderPhone?: string;
};

const getOrder = (state: RootState) => state.entities.order;

const EMPTY_EXTRA_FEES: ExtraFee[] = [];
const EMPTY_CODES: string[] = [];
const EMPTY_INSTRUCTIONS: Instruction[] = [];
const INSTRUCTIONS_ERROR_CODES = [
  INVALID_STUDENT_CNY_CODE,
  PAYER_NOT_SUPPORTED_CODE,
];

const EMPTY_FIELDS: PaymentFields = {
  isPending: false,
  isOnHold: false,
  isDelivered: false,
  isReceived: false,
  isCancelled: false,
  isSent: false,
  isVerification: false,
  isFailed: false,
  isOnline: false,
  isBankTransfer: false,
  isProofNeeded: false,
  isWaitingForInstructions: false,
  isFromAgents: false,
  hasFXFormResponses: false,
  extraFees: EMPTY_EXTRA_FEES,
  instructions: EMPTY_INSTRUCTIONS,
  offer: {} as Offer,
  verificationErrorCodes: EMPTY_CODES,
};

const ONLINE_INSTRUCTIONS = [INSTRUCTION_REDIRECT_URL, INSTRUCTION_EMBEDDED];
const ONLINE_PAYMENT_METHODS = [
  ...BPAY_PAYMENT_METHOD,
  ...BILL_PAY_PAYMENT_METHOD,
];

const toCurrencyAmount = (amount?: Price | string) => {
  if (!amount || typeof amount === 'string') {
    return undefined;
  }

  const { value, currency } = amount;

  return {
    value,
    currency:
      typeof currency === 'string' ? currency : (currency as Currency).code,
  };
};

const getInstructionsErrorCodes = (payment: Payment) => {
  const codes = (payment.instructionsErrors ?? [])
    .map(({ reason }) => reason.code)
    .filter(code => INSTRUCTIONS_ERROR_CODES.includes(code));

  return codes.length ? Array.from(new Set(codes)) : undefined;
};

const getVerificationErrorCodes = (payment: Payment) =>
  payment.documentVerificationFailedReasons?.length
    ? payment.documentVerificationFailedReasons.map(({ code }) => code)
    : EMPTY_CODES;

const getSenderPhone = (payment: Payment) => {
  const value = payment.sender?.fields?.find(({ id }) => id === 'phone')?.value;

  return value ? String(value) : undefined;
};

const derivePaymentFields = (
  payment: Payment,
  recipient: ReturnType<typeof getRecipient>,
): PaymentFields => {
  const status = getUIPaymentStatus(recipient || {}, payment);
  const instructions = payment.instructions as Instruction[];
  const offer = payment.offer ?? ({} as Offer);
  const { brand, lastFourDigits } =
    payment.chargeIntent?.paymentMethodDetails ?? {};

  const links = payment.links as Link[];
  const { cancelledAt, deliveredAt, guaranteedAt } =
    payment.statusTransitions ?? {};

  return {
    status,
    rawStatus: payment.status,
    isPending: status === PAYMENT_STATUS_PENDING,
    isOnHold: status === PAYMENT_STATUS_ON_HOLD,
    isDelivered: status === PAYMENT_STATUS_DELIVERED,
    isReceived: status === PAYMENT_STATUS_RECEIVED,
    isCancelled: status === PAYMENT_STATUS_CANCELLED,
    isSent: status === PAYMENT_STATUS_SENT,
    isVerification: status === PAYMENT_STATUS_VERIFICATION,
    isFailed: status === PAYMENT_STATUS_FAILED,
    isOnline:
      ONLINE_PAYMENT_METHODS.includes(offer.paymentMethodInternalName) ||
      instructions.some(({ name }) => ONLINE_INSTRUCTIONS.includes(name)),
    isBankTransfer: offer.type === 'bank_transfer',
    isProofNeeded:
      payment.status === PAYMENT_STATUS_INITIATED &&
      (links ?? []).some(({ rel }) => rel === 'proofs'),
    isWaitingForInstructions:
      payment.status === PAYMENT_STATUS_INITIATED && instructions.length === 0,
    isFromAgents: payment.metadata?.isAgent ?? false,
    hasFXFormResponses: Object.keys(payment.metadata ?? {}).some(key =>
      key.startsWith('fxFeedback'),
    ),
    price: toCurrencyAmount(payment.price),
    purchase: toCurrencyAmount(payment.purchase),
    fees: toCurrencyAmount(payment.fees),
    extraFees: payment.extraFees?.length ? payment.extraFees : EMPTY_EXTRA_FEES,
    instructions,
    offer,
    paymentOption: offer.paymentOption,
    recurringDetails: payment.recurringDetails && {
      total: payment.recurringDetails.totalInstallments,
      current: payment.recurringDetails.currentInstallment,
    },
    onHoldComment: payment.onHoldComment,
    cardDetails:
      brand && lastFourDigits ? { brand, lastFourDigits } : undefined,
    cancelledAt,
    deliveredAt,
    guaranteedAt,
    verificationErrorCodes: getVerificationErrorCodes(payment),
    instructionsErrorCodes: getInstructionsErrorCodes(payment),
    senderPhone: getSenderPhone(payment),
  };
};

const sourceQuery = (
  source: PaymentSource | undefined,
  locale: string,
  dispatch: ReturnType<typeof useDispatch>,
): UseQueryOptions<Payment, unknown, Payment, QueryKey> =>
  source?.type === 'order'
    ? paymentQueries.byOrder(
        { orderId: source.orderId, token: source.token, locale },
        { dispatch },
      )
    : paymentQueries.byId({ id: source?.id ?? '', locale });

export const usePayment = (source?: PaymentSource) => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const queryClient = useQueryClient();
  const contextSource = usePaymentSource();
  const paymentSource = source ?? contextSource;
  const locale = useSelector(getCurrentLocale);
  const recipient = useSelector(getRecipient);

  const query = useQuery({
    ...sourceQuery(paymentSource, locale, dispatch),
    enabled: false,
  });

  const data = paymentSource ? query.data : undefined;

  const fields = useMemo(
    () => (data ? derivePaymentFields(data, recipient) : EMPTY_FIELDS),
    [data, recipient],
  );

  const fetchPaymentByOrder = useCallback(
    async (
      params: FetchPaymentByOrderParams = {},
    ): Promise<FetchPaymentByOrderResult> => {
      const state = store.getState();
      const currentLocale = getCurrentLocale(state);
      const currentOrder = getOrder(state);
      const { orderId, token } =
        params.orderId && params.token
          ? params
          : { orderId: currentOrder?.id, token: currentOrder?.token };

      await untilIsFetched(store.getState, 'payment');

      if (!orderId || !token) {
        dispatch({ type: FETCH_PAYMENT_REQUEST });
        errorNotifier.notify(
          `${FETCH_PAYMENT_FAILURE} Invalid order id (${orderId}) or token (${token}) when fetching order payment`,
        );
        dispatch({ type: FETCH_PAYMENT_FAILURE });
        return { success: false };
      }

      try {
        const payment = await queryClient.fetchQuery(
          paymentQueries.byOrder(
            { orderId, token, locale: currentLocale },
            { dispatch },
          ),
        );

        return { success: true, payment };
      } catch (_error) {
        return { success: false };
      }
    },
    [dispatch, queryClient, store],
  );

  const refetchPayment = useCallback(async () => {
    await fetchPaymentByOrder();
  }, [fetchPaymentByOrder]);

  return { data, ...fields, fetchPaymentByOrder, refetchPayment };
};
